// src/hooks/useContactPicker.ts
import { useState, useCallback } from 'react';

// Define the interfaces for our types
export interface SelectedContact {
  name: string;
  email?: string;
  phone?: string;
}

export interface UseContactPickerResult {
  isSupported: boolean;
  pickContacts: () => Promise<void>;
  selectedContacts: SelectedContact[];
  error: Error | null;
}

export const useContactPicker = (): UseContactPickerResult => {
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>([]);
  const [error, setError] = useState<Error | null>(null);

  // Check if the Contact Picker API is supported
  const isSupported = 'contacts' in navigator && 'ContactsManager' in window;

  const pickContacts = useCallback(async () => {
    if (!isSupported) {
      setError(new Error('Contact Picker API not supported in this browser'));
      return;
    }

    try {
      // @ts-ignore - TypeScript doesn't have built-in types for the Contact Picker API
      const contacts = await navigator.contacts.select(
        ['name', 'email', 'tel'],
        { multiple: true }
      );

      // Format the contacts into our expected structure
      const formattedContacts: SelectedContact[] = contacts.map((contact: any) => ({
        name: contact.name?.[0] || 'Unknown',
        email: contact.email?.[0],
        phone: contact.tel?.[0]
      }));

      setSelectedContacts(formattedContacts);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to pick contacts'));
    }
  }, [isSupported]);

  return { isSupported, pickContacts, selectedContacts, error };
};